/** @module @liquidefy/code-editor/layout/layout-minimap @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  ViewportState,
  MinimapConfig,
  DecorationData,
  TokenType,
  Range,
  DiffChange,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  clamp,
} from '../code-editor.js';

/**
 * Computed minimap viewport state.
 */
export interface MinimapViewportState {
  readonly startLine: number;
  readonly endLine: number;
  readonly scale: number;
  readonly pixelRatio: number;
  readonly height: number;
}

/**
 * Minimap sampling configuration for very large files.
 */
interface MinimapSampling {
  readonly sampleEvery: number;
  readonly sampledLineCount: number;
}

/**
 * Positioned minimap decoration for rendering.
 */
interface MinimapDecorationPosition {
  readonly decorationId: string;
  readonly y: number;
  readonly height: number;
  readonly color: string;
  readonly position: 'inline' | 'gutter';
}

/**
 * Highlight band in the minimap (e.g., search results).
 */
interface MinimapHighlight {
  readonly y: number;
  readonly height: number;
  readonly color: string;
}

/**
 * Diff indicator in the minimap gutter.
 */
interface MinimapDiffIndicator {
  readonly y: number;
  readonly height: number;
  readonly kind: 'added' | 'modified' | 'deleted';
}

// ═══ §2 MINIMAP VIEWPORT — compute minimap dimensions, slider position and size ═══

/**
 * Computes the minimap viewport based on the editor viewport and document.
 */
export function computeMinimapViewport(
  editorViewport: ViewportState,
  totalLines: number,
  config: MinimapConfig,
): EditorResult<MinimapViewportState> {
  if (!config.enabled) {
    return err('MINIMAP_DISABLED', 'Minimap is not enabled');
  }

  const scale = clamp(config.scale, 0.1, 3);
  const minimapLH = minimapLineHeight(scale);
  const minimapH = editorViewport.height;
  const maxVisibleLines = Math.floor(minimapH / minimapLH);

  // Center the minimap around the editor viewport center
  const editorCenter = Math.floor((editorViewport.startLine + editorViewport.endLine) / 2);
  const halfVisible = Math.floor(maxVisibleLines / 2);

  let startLine = clamp(editorCenter - halfVisible, 0, Math.max(0, totalLines - maxVisibleLines));
  let endLine = Math.min(startLine + maxVisibleLines, totalLines);

  // If document is shorter than minimap can show, show all
  if (totalLines <= maxVisibleLines) {
    startLine = 0;
    endLine = totalLines;
  }

  return ok({
    startLine,
    endLine,
    scale,
    pixelRatio: 1,
    height: minimapH,
  }, 'MINIMAP_VIEWPORT', 'Minimap viewport computed');
}

/**
 * Returns the rendered line height in the minimap for the given scale.
 */
export function minimapLineHeight(scale: number): number {
  return Math.max(1, Math.round(2 * scale));
}

/**
 * Returns the rendered character width in the minimap for the given scale.
 */
export function minimapCharWidth(scale: number): number {
  return Math.max(1, Math.round(1.2 * scale));
}

/**
 * Returns the total pixel width of the minimap.
 */
export function minimapWidth(config: MinimapConfig, contentMaxColumn: number): number {
  if (!config.enabled) return 0;
  const effectiveColumn = Math.min(contentMaxColumn, config.maxColumn);
  const charW = minimapCharWidth(config.scale);
  // Add gutter padding
  return effectiveColumn * charW + 8;
}

/**
 * Computes the Y position of the minimap slider (the highlighted viewport indicator).
 */
export function minimapSliderPosition(
  editorViewport: ViewportState,
  totalLines: number,
  minimapHeight: number,
): number {
  if (totalLines <= 0) return 0;
  const ratio = editorViewport.startLine / totalLines;
  return Math.round(ratio * minimapHeight);
}

/**
 * Computes the height of the minimap slider.
 */
export function minimapSliderHeight(
  editorViewport: ViewportState,
  totalLines: number,
  minimapHeight: number,
): number {
  if (totalLines <= 0) return minimapHeight;
  const visibleLines = editorViewport.endLine - editorViewport.startLine;
  const ratio = visibleLines / totalLines;
  return Math.max(10, Math.round(ratio * minimapHeight));
}

// ═══ §3 MINIMAP RENDERING — line-to-pixel mapping, character rendering decisions, sampling, token colors ═══

/**
 * Converts a minimap line number to its Y pixel position within the minimap.
 */
export function minimapLineToPixelY(line: number, minimapViewport: MinimapViewportState): number {
  const lh = minimapLineHeight(minimapViewport.scale);
  return (line - minimapViewport.startLine) * lh;
}

/**
 * Converts a pixel Y position in the minimap to a line number.
 */
export function pixelYToMinimapLine(y: number, minimapViewport: MinimapViewportState): number {
  const lh = minimapLineHeight(minimapViewport.scale);
  if (lh <= 0) return minimapViewport.startLine;
  return Math.floor(y / lh) + minimapViewport.startLine;
}

/**
 * Determines whether the minimap should render individual characters
 * at the current scale. Below a certain threshold, blocks are used instead.
 */
export function shouldRenderCharacters(config: MinimapConfig, scale: number): boolean {
  if (!config.renderCharacters) return false;
  return scale >= 1;
}

/**
 * For very long files, computes a sampling rate so the minimap remains performant.
 * Returns the sampling interval and the resulting sampled line count.
 */
export function computeMinimapSampling(
  totalLines: number,
  minimapHeight: number,
  lineHeight: number,
): MinimapSampling {
  if (lineHeight <= 0) return { sampleEvery: 1, sampledLineCount: totalLines };

  const maxRenderable = Math.floor(minimapHeight / lineHeight);
  if (totalLines <= maxRenderable) {
    return { sampleEvery: 1, sampledLineCount: totalLines };
  }

  const sampleEvery = Math.ceil(totalLines / maxRenderable);
  const sampledLineCount = Math.ceil(totalLines / sampleEvery);

  return { sampleEvery, sampledLineCount };
}

/**
 * Returns a simplified hex color for a token type in the minimap.
 * These are muted, low-contrast colors suitable for the minimap's small scale.
 */
export function minimapColorForToken(
  tokenType: TokenType,
  theme: 'light' | 'dark',
): string {
  const darkColors: Record<TokenType, string> = {
    keyword: '#569cd6',
    identifier: '#9cdcfe',
    string: '#ce9178',
    number: '#b5cea8',
    operator: '#d4d4d4',
    comment: '#6a9955',
    punctuation: '#808080',
    type: '#4ec9b0',
    decorator: '#dcdcaa',
    regex: '#d16969',
    template: '#ce9178',
    embedded: '#808080',
    whitespace: '#3c3c3c',
    invalid: '#f44747',
  };

  const lightColors: Record<TokenType, string> = {
    keyword: '#0000ff',
    identifier: '#001080',
    string: '#a31515',
    number: '#098658',
    operator: '#000000',
    comment: '#008000',
    punctuation: '#505050',
    type: '#267f99',
    decorator: '#795e26',
    regex: '#811f3f',
    template: '#a31515',
    embedded: '#505050',
    whitespace: '#e0e0e0',
    invalid: '#cd3131',
  };

  return theme === 'dark' ? darkColors[tokenType] : lightColors[tokenType];
}

// ═══ §4 MINIMAP DECORATIONS — filter, position, search highlights, diff indicators ═══

/**
 * Filters decorations to those visible in the minimap line range.
 */
export function minimapDecorationsInRange(
  decorations: DecorationData[],
  startLine: number,
  endLine: number,
): DecorationData[] {
  return decorations.filter((d) => {
    if (!d.options.minimap) return false;
    return d.range.end.line >= startLine && d.range.start.line < endLine;
  });
}

/**
 * Computes the pixel position of a decoration within the minimap.
 */
export function minimapDecorationPosition(
  decoration: DecorationData,
  minimapViewport: MinimapViewportState,
): MinimapDecorationPosition {
  const lh = minimapLineHeight(minimapViewport.scale);
  const y = (decoration.range.start.line - minimapViewport.startLine) * lh;
  const lineSpan = decoration.range.end.line - decoration.range.start.line + 1;
  const height = Math.max(lh, lineSpan * lh);

  return {
    decorationId: decoration.id as string,
    y,
    height,
    color: decoration.options.minimap?.color ?? '#808080',
    position: decoration.options.minimap?.position ?? 'inline',
  };
}

/**
 * Computes minimap highlight bands for search matches.
 */
export function computeMinimapHighlights(
  searchMatches: Range[],
  minimapViewport: MinimapViewportState,
): MinimapHighlight[] {
  const lh = minimapLineHeight(minimapViewport.scale);
  const highlights: MinimapHighlight[] = [];

  for (const match of searchMatches) {
    if (match.end.line < minimapViewport.startLine) continue;
    if (match.start.line >= minimapViewport.endLine) continue;

    const y = (match.start.line - minimapViewport.startLine) * lh;
    const lineSpan = match.end.line - match.start.line + 1;

    highlights.push({
      y: Math.max(0, y),
      height: Math.max(lh, lineSpan * lh),
      color: '#e8ab53',
    });
  }

  return highlights;
}

/**
 * Computes minimap diff indicators from diff hunks.
 */
export function computeMinimapDiffIndicators(
  diffHunks: DiffChange[],
  minimapViewport: MinimapViewportState,
): MinimapDiffIndicator[] {
  const lh = minimapLineHeight(minimapViewport.scale);
  const indicators: MinimapDiffIndicator[] = [];

  for (const hunk of diffHunks) {
    const startLine = hunk.modifiedStartLineNumber;
    const endLine = hunk.modifiedEndLineNumber;

    if (endLine < minimapViewport.startLine) continue;
    if (startLine >= minimapViewport.endLine) continue;

    const y = (startLine - minimapViewport.startLine) * lh;
    const lineSpan = endLine - startLine + 1;

    let kind: 'added' | 'modified' | 'deleted';
    if (hunk.originalStartLineNumber === hunk.originalEndLineNumber &&
        hunk.originalStartLineNumber === 0) {
      kind = 'added';
    } else if (hunk.modifiedStartLineNumber === hunk.modifiedEndLineNumber &&
               hunk.modifiedStartLineNumber === 0) {
      kind = 'deleted';
    } else {
      kind = 'modified';
    }

    indicators.push({
      y: Math.max(0, y),
      height: Math.max(lh, lineSpan * lh),
      kind,
    });
  }

  return indicators;
}

// ═══ §5 MINIMAP INTERACTION — click-to-line, drag-to-scroll, slider hit testing ═══

/**
 * Converts a click Y position on the minimap to a document line number.
 */
export function minimapClickToLine(
  y: number,
  minimapViewport: MinimapViewportState,
  totalLines: number,
): number {
  const line = pixelYToMinimapLine(y, minimapViewport);
  return clamp(line, 0, Math.max(0, totalLines - 1));
}

/**
 * During a minimap drag operation, computes the target scroll line
 * based on the drag delta from the start Y position.
 */
export function minimapDragToScroll(
  startY: number,
  currentY: number,
  minimapViewport: MinimapViewportState,
  totalLines: number,
): number {
  const lh = minimapLineHeight(minimapViewport.scale);
  if (lh <= 0) return 0;

  const deltaLines = Math.round((currentY - startY) / lh);
  const startLine = pixelYToMinimapLine(startY, minimapViewport);
  const targetLine = startLine + deltaLines;

  return clamp(targetLine, 0, Math.max(0, totalLines - 1));
}

/**
 * Tests whether a Y coordinate falls within the minimap slider area.
 */
export function isInMinimapSlider(
  y: number,
  sliderPos: number,
  sliderHeight: number,
): boolean {
  return y >= sliderPos && y <= sliderPos + sliderHeight;
}
